import type { Coordinate, MultiPolygonCoordinates } from '../models/editor-feature';

/**
 * Rotating a desk-shape polygon's actual vertices.
 *
 * Plain two-dimensional trigonometry around the shape's own centroid. These coordinates are
 * each project's own arbitrary drawing units — not longitude and latitude — so a geodesic
 * rotation helper is not merely imprecise here, it is wrong by orders of magnitude: fed
 * planar coordinates it treats them as degrees, and a desk shape only measures about 0.2
 * units across.
 *
 * This is the one rotation in the editor that touches geometry. A desk marker or an
 * annotation rotates by drawing alone, because a point has no shape to turn.
 */

const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;

const isSameCoordinate = (first: Coordinate, second: Coordinate): boolean => first[0] === second[0]
  && first[1] === second[1];

/**
 * Every vertex of a MultiPolygon, flattened, with each ring's closing vertex dropped.
 *
 * A GeoJSON ring repeats its first vertex at the end to close itself. Counting that repeat
 * pulls the mean towards one corner — for a unit square it lands 0.1 units off centre — and
 * a rotation about a pivot that is not the true centre walks the shape across the plan
 * instead of turning it in place.
 */
const verticesOf = (coordinates: MultiPolygonCoordinates): Coordinate[] => coordinates
  .flatMap((polygon) => polygon.flatMap((ring) => {
    const closed = ring.length > 1 && isSameCoordinate(ring[0], ring[ring.length - 1]);
    return closed ? ring.slice(0, -1) : ring;
  }));

export const multiPolygonCentroid = (coordinates: MultiPolygonCoordinates): Coordinate => {
  const vertices = verticesOf(coordinates);

  if (!vertices.length) {
    return [0, 0];
  }

  const sum = vertices.reduce<[number, number]>(
    (total, [x, y]) => [total[0] + x, total[1] + y],
    [0, 0],
  );

  return [sum[0] / vertices.length, sum[1] / vertices.length];
};

export const rotateCoordinateAround = (
  coordinate: Coordinate,
  pivot: Coordinate,
  degrees: number,
): Coordinate => {
  const radians = toRadians(degrees);
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  const offsetX = coordinate[0] - pivot[0];
  const offsetY = coordinate[1] - pivot[1];

  return [
    pivot[0] + offsetX * cos - offsetY * sin,
    pivot[1] + offsetX * sin + offsetY * cos,
  ];
};

/**
 * Rotates a shape by a relative angle around its own centroid, preserving its nesting.
 *
 * The nesting matters: every desk shape in this data is a MultiPolygon, and code written
 * for one nesting depth reads another as NaN without throwing, so the structure is mapped
 * rather than flattened and rebuilt.
 */
export const rotateMultiPolygon = (
  coordinates: MultiPolygonCoordinates,
  degrees: number,
  pivot: Coordinate = multiPolygonCentroid(coordinates),
): MultiPolygonCoordinates => coordinates.map(
  (polygon) => polygon.map(
    (ring) => ring.map((vertex) => rotateCoordinateAround(vertex, pivot, degrees)),
  ),
);

export const translateCoordinate = (coordinate: Coordinate, deltaX: number, deltaY: number): Coordinate => [
  coordinate[0] + deltaX,
  coordinate[1] + deltaY,
];

export const translateMultiPolygon = (
  coordinates: MultiPolygonCoordinates,
  deltaX: number,
  deltaY: number,
): MultiPolygonCoordinates => coordinates.map(
  (polygon) => polygon.map(
    (ring) => ring.map((vertex) => translateCoordinate(vertex, deltaX, deltaY)),
  ),
);

/**
 * Scales a shape about a pivot, preserving its nesting.
 *
 * Like rotation, this moves real vertices: a desk drawing has a size in the plan's own
 * coordinates, so making one bigger means the polygon itself grows. Nothing about it is a
 * display transform.
 */
export const scaleMultiPolygon = (
  coordinates: MultiPolygonCoordinates,
  factor: number,
  pivot: Coordinate = multiPolygonCentroid(coordinates),
): MultiPolygonCoordinates => coordinates.map(
  (polygon) => polygon.map(
    (ring) => ring.map(([x, y]): Coordinate => [
      pivot[0] + (x - pivot[0]) * factor,
      pivot[1] + (y - pivot[1]) * factor,
    ]),
  ),
);

/** Degrees wrapped into [0, 360), so an angle shown in the inspector never reads as -450. */
export const normalizeDegrees = (degrees: number): number => ((degrees % 360) + 360) % 360;
